import React from 'react';
import {injectIntl} from 'react-intl';
import {HeaderShell, Stepper} from 'wobi-web-common/dist/v2/index.js';
import {withRouter} from 'react-router-dom';
import {fireGtmEvent} from '../utils/marketing.utils';
import {getStepName, getQuoteProgressIndex} from '../utils/routingHelpers';
import {agentDeveloperUtils, Helpers} from 'wobi-web-common';
import routes from '../config/routes';
import PropTypes from 'prop-types';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import {createProgressSteps, QUOTE_PROGRESS_STEPS} from '../config/progressSteps';
import {AI_CHAT_ENABLED} from '../config/features';
import AiHeaderButton from './AiHeaderButton';
import './Header.css';

const CompanyLogo = Helpers.getImageByAgent('companyLogo.svg');

// Refine the popstate listener to clear only specific session storage keys and redirect only when necessary
window.addEventListener('popstate', () => {
  // Handle specific paths to avoid invalid states
  if ('/offers' === window.location.pathname) {
    sessionStorage.removeItem('travel.offers-tab');
    return;
  }

  if ('/details' === window.location.pathname && '#extra' === window.location.hash) {
    sessionStorage.removeItem('travel.details-form');
  }
}, false);

class Header extends React.PureComponent {
  state = {
    stepIndex: 0,
    showSteps: true,
    isMainTitleVisible: routes ? routes.includes(this.props.location.pathname) : true,
  };

  handleProgressStepClick = (index, step) => {
    if (index >= this.state.stepIndex || step.disabled || !step.target) {
      return;
    }

    this.props.history.push(step.target);
  };

  componentDidMount() {
    this.setStepAndPercentage(this.props.location);
    this.unlisten = this.props.history.listen((location) => {
      this.setStepAndPercentage(location);
    });
  }

  componentDidUpdate(previousProps) {
    if (previousProps.location !== this.props.location) {
      this.setStepAndPercentage(this.props.location);
    }
  }

  componentWillUnmount() {
    this.unlisten();
  }

  trackBackNavigation = (location) => {
    const previous = this.previousLocation;
    this.previousLocation = location;
    if (!previous || previous === location) {
      return;
    }

    const previousStep = getStepName(previous);
    const previousIndex = previousStep === 'details'
      ? getQuoteProgressIndex(previous)
      : ['offers', 'complete-details', 'payment'].includes(previousStep) ? Infinity : -1;
    if (getStepName(location) !== 'details') {
      return;
    }

    const currentIndex = getQuoteProgressIndex(location);
    if (previousIndex <= currentIndex) {
      return;
    }

    if (currentIndex === 0) {
      fireGtmEvent('BackToDatesAndDestination');
    } else if (currentIndex === 1) {
      fireGtmEvent('BackToPersonalDetails');
    } else if (currentIndex === 2) {
      fireGtmEvent('BackToMedical');
    }
  };

  setStepAndPercentage = (location) => {
    this.trackBackNavigation(location);
    const step = getStepName(location);

    if (step !== 'details') {
      this.setState({showSteps: false});
      return;
    }

    this.setState({
      showSteps: true,
      stepIndex: getQuoteProgressIndex(location),
    });
  };

  render() {
    const {intl} = this.props;
    const progressSteps = createProgressSteps(
      QUOTE_PROGRESS_STEPS,
      this.state.stepIndex,
      messageId => intl.formatMessage({id: messageId}),
    );
    const agentEmail = agentDeveloperUtils.getAgentEmail;
    const agentDisplayName = agentDeveloperUtils.getAgentDisplayName;
    const showLogo = agentDeveloperUtils.isWebSiteEnabled;
    const openAiChat = () => {
      fireGtmEvent('TravelAIHeader');
      window.dispatchEvent(new CustomEvent('wobi-open-ai'));
    };

    const brand = showLogo ? (
      <a
        className='travel-header__logo-link'
        title={agentDeveloperUtils.websiteConfig.headerIconTooltip}
        href={agentDeveloperUtils.serviceWebsiteUrl}
        onClick={() => fireGtmEvent('Travel2HomePage')}
      >
        <img className='travel-header__logo' src={CompanyLogo} alt={`${agentDisplayName} logo`} />
      </a>
    ) : null;

    const actions = (
      <>
        {agentEmail && (
          <a
            className='travel-header__email'
            href={`mailto:${agentEmail}`}
            aria-label={`קישור למייל שלנו ${agentEmail}`}
            onClick={() => fireGtmEvent('TravelCall')}
          >
            <span className='travel-header__email-icon' aria-hidden='true'>
              <MailOutlineIcon />
            </span>
            <span className='travel-header__email-text'>{agentEmail}</span>
          </a>
        )}
        {AI_CHAT_ENABLED ? <AiHeaderButton onClick={openAiChat} /> : null}
      </>
    );

    return (
      <React.Fragment>
        <HeaderShell
          ariaLabel='כותרת האתר'
          brand={brand}
          productLabel={intl.formatMessage({id: 'travelInsurance'})}
          actions={actions}
          sticky
        />
        {
          this.state.showSteps && (
            <Stepper
              className='travel-progress'
              steps={progressSteps}
              currentStep={this.state.stepIndex}
              onStepClick={this.handleProgressStepClick}
              ariaLabel={intl.formatMessage({id: 'progress.quote.ariaLabel'})}
              style={{maxWidth: 560, margin: '0 auto', padding: 'clamp(16px, 2.4vw, 24px) clamp(18px, 4vw, 48px) 0'}}
            />
          )
        }
      </React.Fragment>
    );
  }
}

// Add prop type validations
Header.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  history: PropTypes.shape({
    goBack: PropTypes.func.isRequired,
    listen: PropTypes.func.isRequired,
    push: PropTypes.func.isRequired,
  }).isRequired,
  isMobile: PropTypes.bool,
  intl: PropTypes.object.isRequired,
};

export {Header};
export default withRouter(injectIntl(Header));
